import React, { FunctionComponent, useContext, useMemo } from 'react';
import { PaginationProps } from 'ui/molecules/pagination';
import {
  ProductPermissionEnum,
  InvestorProductDocument,
  InvestorProductDocumentsApi,
  AdminInvestorProductDocumentsListOrderingEnum,
  PureDocumentType,
  AdminInvestorProductDocumentsValuesRetrieveFieldEnum,
} from 'src/api';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import WithDataRecord from 'src/hoc/WithDataRecord';
import Translate from 'src/ui/atoms/translate';
import Link from 'ui/atoms/link';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import Tooltip from 'src/ui/atoms/tooltip';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { Search } from 'src/ui/molecules/filtering';

export interface DocumentsTableProps {
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  paginationProps?: PaginationProps;
  loading?: boolean;
  hasPermission: boolean;
  onDeleteButtonClick: (document: InvestorProductDocument) => void;
  setFilterPopupHeight?: (height: number) => void;
}

const DocumentsTable: FunctionComponent<DocumentsTableProps> = WithDataRecord<
  InvestorProductDocumentsApi,
  InvestorProductDocument
>(InvestorProductDocumentsApi)(
  ({
    data,
    ordering,
    onOrderBy,
    paginationProps,
    onDeleteButtonClick,
    hasPermission,
    loading,
    setFilterPopupHeight,
  }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const investorProductDocumentsApi: InvestorProductDocumentsApi = useMemo(
      () => makeAuthenticatedApi(InvestorProductDocumentsApi),
      [makeAuthenticatedApi],
    );

    const tokenHasManageDocumentsPermission = (permissions: ProductPermissionEnum[]) =>
      permissions.includes(ProductPermissionEnum.MANAGE_INVESTOR_DOCUMENTS);

    const { activeLocale } = useContext(I18NContext);

    const documentsOptions = Object.values(PureDocumentType).map((documentType) => ({
      id: documentType,
      name: translate(`documentTypes.${snakeToCamelCaseWithNumbers(documentType)}`),
    }));

    return (
      <>
        <DataRecordTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          data={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          setFilterPopupHeight={setFilterPopupHeight}
          searchComponent={
            <Search input="search" defaultField="search" label={translate('filters.labels.documents')} name="search" />
          }
          cells={[
            {
              title: <Translate name={`studioDocumentsTable.documentName`} />,
              render: ({ document }) => document.name,
              orderingFieldName: AdminInvestorProductDocumentsListOrderingEnum.DOCUMENT_NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'documentName',
              searchFieldName: AdminInvestorProductDocumentsValuesRetrieveFieldEnum.DOCUMENT_NAME,
              getFilterData: (params: any) =>
                investorProductDocumentsApi.adminInvestorProductDocumentsValuesRetrieve(params),
            },
            {
              title: <Translate name={`studioDocumentsTable.account`} />,
              render: ({ investorName }) => investorName,
              orderingFieldName: AdminInvestorProductDocumentsListOrderingEnum.INVESTOR_NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'investorName',
              searchFieldName: AdminInvestorProductDocumentsValuesRetrieveFieldEnum.INVESTOR_NAME,
              getFilterData: (params: any) =>
                investorProductDocumentsApi.adminInvestorProductDocumentsValuesRetrieve(params),
            },
            {
              title: <Translate name={`studioDocumentsTable.documentType`} />,
              render: ({ document }) => (
                <Translate name={`documentTypes.${snakeToCamelCaseWithNumbers(document.documentType || '')}`} />
              ),
              orderingFieldName: AdminInvestorProductDocumentsListOrderingEnum.DOCUMENT_TYPE_ASC,
              type: CellTypeEnum.text,
              filterName: 'documentType',
              getFilterData: () => {
                return documentsOptions;
              },
            },
            {
              title: <Translate name={`studioDocumentsTable.product`} />,
              render: ({ productName }) => productName,
              orderingFieldName: AdminInvestorProductDocumentsListOrderingEnum.PRODUCT_NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'productName',
              searchFieldName: AdminInvestorProductDocumentsValuesRetrieveFieldEnum.PRODUCT_NAME,
              getFilterData: (params: any) =>
                investorProductDocumentsApi.adminInvestorProductDocumentsValuesRetrieve(params),
            },
            {
              title: <Translate name={`studioDocumentsTable.actions`} />,
              render: (investorDocument) => (
                <ActionButtons align="start" spacingTop="none">
                  <Link
                    href={investorDocument.document.file}
                    target="_blank"
                    icon="download"
                    download={true}
                    iconColor="primary"
                    iconSize="large"
                  />
                  {tokenHasManageDocumentsPermission(investorDocument.productPermissions) && (
                    <Button variant="secondary" onClick={() => onDeleteButtonClick(investorDocument)} size="xsmall">
                      <Translate name="common.delete" />
                    </Button>
                  )}
                  {!tokenHasManageDocumentsPermission(investorDocument.productPermissions) && (
                    <Tooltip maxWidth={188} content={<Translate name="studioDocumentsTable.noDeletePermission" />}>
                      <Button variant="secondary" disabled size="xsmall">
                        <Translate name="common.delete" />
                      </Button>
                    </Tooltip>
                  )}
                </ActionButtons>
              ),
              width: activeLocale === 'de' ? 152 : 140,
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminInvestorProductDocumentsList({
      offset,
      limit,
      ordering: ordering as AdminInvestorProductDocumentsListOrderingEnum,
      ...props,
    });
  },
);

export default DocumentsTable;
